/**
 * Education360 API client - isolated axios instance.
 * Never talks to THOM360 endpoints.
 */
import axios from "axios";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/education360`;

const client = axios.create({ baseURL: BASE });

client.interceptors.request.use((config) => {
  const token = localStorage.getItem("edu360.token");
  const tenant = localStorage.getItem("edu360.tenant_slug");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (tenant) config.headers["X-Tenant-Slug"] = tenant;
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("edu360.token");
      localStorage.removeItem("edu360.user");
    }
    return Promise.reject(err);
  }
);

export default client;

export const api = {
  // tenant
  tenantBySlug: (slug) => client.get(`/tenants/by-slug/${slug}`),
  tenants: () => client.get("/tenants"),
  // auth
  login: (data) => client.post("/auth/login", data),
  me: () => client.get("/auth/me"),
  // executive
  execKpis: () => client.get("/executive/kpis"),
  execTrend: () => client.get("/executive/attendance-trend"),
  execInsights: () => client.get("/executive/insights"),
  execGenerate: () => client.post("/executive/generate-insight", {}),
  // Academic Calendar Service (Fork 1 · canonical current-period authority)
  calendarCurrent: () => client.get("/calendar/current"),
  calendarConfig: () => client.get("/calendar/config"),
  calendarUpdate: (patch) => client.put("/calendar/config", patch),
  calendarOverride: (payload) => client.post("/calendar/override", payload),
  calendarOverrideClear: () => client.delete("/calendar/override"),
  calendarAudit: () => client.get("/calendar/audit"),
  // School Administration
  adminUpdateInstitution: (patch) => client.put("/school-admin/institution", patch),
  adminUpdateBranding: (patch) => client.put("/school-admin/branding", patch),
  adminAudit: () => client.get("/school-admin/audit"),
  adminTimetableConfigGet: () => client.get("/school-admin/timetable-config"),
  adminTimetableConfigPut: (patch) => client.put("/school-admin/timetable-config", patch),
  adminTimetableAudit: () => client.get("/school-admin/timetable-audit"),
  pastoralQueue: () => client.get("/pastoral/review-queue"),
  pastoralReview: (studentId, payload) => client.post(`/pastoral/review/${studentId}`, payload),
  admissionsProspects: () => client.get("/admissions/prospects"),
  admissionsFunnel: () => client.get("/admissions/funnel"),
  medicalEvents: (studentId) => client.get("/medical/events", { params: studentId ? { student_id: studentId } : {} }),
  medicalSummary: () => client.get("/medical/summary"),
  // students / people
  //
  // Canonical population access — Highlands has 1,255 active students; we default
  // to `limit=2000` so every consumer (Students list, Publishing Centre, Report
  // Cards, Grade Books) sees the full population and can search / select any
  // student regardless of enrolment order. Callers who need a paged view can
  // override `limit`/`skip` in params. `total` from the backend is preserved so
  // KPIs never derive institution-wide counts from a truncated array.
  students: async (params) => {
    const finalParams = { limit: 2000, ...(params || {}) };
    const { data } = await client.get("/students", { params: finalParams });
    if (Array.isArray(data)) return { data, total: data.length };
    return {
      data: data.items || [],
      total: data.total ?? (data.items?.length || 0),
      limit: data.limit,
      skip: data.skip,
    };
  },
  student: (id) => client.get(`/students/${id}`),
  parents: () => client.get("/parents"),
  staff: () => client.get("/staff"),
  // academics
  classes: () => client.get("/classes"),
  subjects: () => client.get("/subjects"),
  timetable: (params) => client.get("/timetable", { params }),
  attendance: (params) => client.get("/attendance", { params }),
  markAttendance: (entries) => client.post("/attendance/mark", { entries }),
  assessments: (params) => client.get("/assessments", { params }),
  grades: (params) => client.get("/grades", { params }),
  reportCard: (studentId) => client.get(`/report-cards/${studentId}`),
  premiumReport: (studentId) => client.get(`/report-cards/${studentId}/premium`),
  // Executive-grade A4 PDF (server-rendered, deterministic, HMAC-signed)
  premiumReportPdfUrl: (studentId) => `${process.env.REACT_APP_BACKEND_URL}/api/education360/report-cards/${studentId}/premium.pdf`,
  premiumReportPdfBlob: (studentId) =>
    client.get(`/report-cards/${studentId}/premium.pdf`, { responseType: "blob" }),
  // Publishing
  publishingStudentPdf: (studentId) => `${process.env.REACT_APP_BACKEND_URL}/api/education360/publishing/student/${studentId}/pdf`,
  publishStudent: (studentId) => client.post(`/publishing/student/${studentId}/publish`),
  archive: (params) => client.get("/publishing/archive", { params }),
  archiveMeta: (docId) => client.get(`/publishing/archive/${docId}`),
  archivePdfUrl: (docId) => `${process.env.REACT_APP_BACKEND_URL}/api/education360/publishing/archive/${docId}/pdf`,
  startBatch: (payload) => client.post("/publishing/batch", payload),
  listJobs: () => client.get("/publishing/jobs"),
  jobStatus: (id) => client.get(`/publishing/jobs/${id}`),
  boardPackPreview: () => client.get("/publishing/board-pack/preview"),
  boardPackPublish: () => client.post("/publishing/board-pack/publish"),
  boardPackList: () => client.get("/publishing/board-pack"),
  // Credentials
  credentialTypes: () => client.get("/publishing/credentials/types"),
  issueCredential: (studentId, type) => client.post(`/publishing/credentials/${studentId}/${type}`),
  // Progress Intelligence
  studentProgress: (studentId) => client.get(`/publishing/progress/${studentId}`),
  // Distribution
  distribute: (docId, payload) => client.post(`/publishing/distribute/${docId}`, payload),
  distributionList: (params) => client.get("/publishing/distribute", { params }),
  // Designer
  designer: () => client.get("/publishing/designer"),
  updateDesigner: (payload) => client.patch("/publishing/designer", payload),
  // Bulk ZIP
  bulkZip: (ids) => `${process.env.REACT_APP_BACKEND_URL}/api/education360/publishing/archive/bulk-download`,
  // Verify — PUBLIC (no auth). Use raw fetch, not the auth-carrying client.
  verify: async (verificationId) => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/education360/verify/${verificationId}`);
    return { data: await res.json() };
  },
  // Attendance360
  attendanceRoster: (classId, date) => client.get(`/attendance/roster?class_id=${classId}${date ? `&date=${date}` : ""}`),
  attendanceAnalytics: () => client.get("/attendance/analytics"),
  attendanceAiBriefing: () => client.post("/attendance/ai-briefing"),
  boardingRoll: (params) => client.get("/attendance/boarding-roll", { params }),
  markBoarding: (entries) => client.post("/attendance/boarding-roll/mark", { entries }),
  transportManifest: (params) => client.get("/attendance/transport-manifest", { params }),
  alertParents: (payload) => client.post("/attendance/alert-parents", payload),
  // Timetable360
  createTimetable: (payload) => client.post("/timetable", payload),
  updateTimetable: (id, payload) => client.patch(`/timetable/${id}`, payload),
  deleteTimetable: (id) => client.delete(`/timetable/${id}`),
  timetableConflicts: () => client.get("/timetable/conflicts"),
  listSubstitutions: () => client.get("/timetable/substitutions"),
  createSubstitution: (payload) => client.post("/timetable/substitutions", payload),
  notifySubstitution: (id) => client.post(`/timetable/substitutions/${id}/notify`),
  publishTimetable: (classId) => client.post(`/timetable/publish/${classId}`),
  // Boarding leave
  createLeave: (payload) => client.post("/boarding/leave", payload),
  returnLeave: (id) => client.post(`/boarding/leave/${id}/return`),
  ackLeave: (id) => client.post(`/boarding/leave/${id}/ack`),
  listLeave: () => client.get("/boarding/leave"),
  // Transport GPS
  transportGps: (routeId) => client.get(`/transport/gps/${routeId}`),
  // Search
  searchArchive: (params) => client.get("/publishing/search", { params }),
  // Bulk credentials
  bulkCredentials: (payload) => client.post("/publishing/credentials/bulk", payload),
  // Parent vault
  parentVault: () => client.get("/publishing/vault"),
  // Secure link revoke
  revokeLink: (id) => client.post(`/publishing/distribute/${id}/revoke`),
  // Tamper check
  tamperCheck: (verificationId) => client.get(`/publishing/tamper-check/${verificationId}`),
  // Public download link (no auth)
  secureLinkUrl: (entryId, token) => `${process.env.REACT_APP_BACKEND_URL}/api/education360/publishing/link/${entryId}?token=${encodeURIComponent(token)}`,
  // Digital Student Passport
  passport: (studentId) => client.get(`/passport/${studentId}`),
  passportParentChildren: () => client.get("/passport/parent/children"),
  // Finance360
  feeBundles: () => client.get("/finance360/fee-bundles"),
  studentStatement: (studentId) => client.get(`/finance360/statement/${studentId}`),
  parentFinanceSummary: () => client.get("/finance360/parent/summary"),
  listReceipts: (params) => client.get("/finance360/receipts", { params }),
  recordReceipt: (payload) => client.post("/finance360/receipts", payload),
  financeAnalytics: () => client.get("/finance360/analytics"),
  // Central Metrics Service / Executive Intelligence Centre
  execSnapshot: () => client.get("/executive-intel/snapshot"),
  execAlerts: () => client.get("/executive-intel/alerts"),
  execForecast: () => client.get("/executive-intel/forecast"),
  execConfig: () => client.get("/executive-intel/config"),
  updateExecConfig: (payload) => client.patch("/executive-intel/config", payload),
  drillFinance: () => client.get("/executive-intel/drill/finance"),
  drillAttendance: () => client.get("/executive-intel/drill/attendance"),
  drillAcademic: () => client.get("/executive-intel/drill/academic"),
  drillAdmissions: () => client.get("/executive-intel/drill/admissions"),
  drillStudentLife: () => client.get("/executive-intel/drill/studentlife"),
  // Principal's Morning Briefing
  briefingLatest: () => client.get("/morning-briefing/latest"),
  briefingHistory: (limit = 30) => client.get(`/morning-briefing/history?limit=${limit}`),
  briefingDetail: (id) => client.get(`/morning-briefing/${id}`),
  briefingGenerateNow: (payload = { send_email: false }) => client.post("/morning-briefing/generate-now", payload),
  // Student Life360
  lifeEvents: (params) => client.get("/life/events", { params }),
  logLifeEvent: (payload) => client.post("/life/events", payload),
  lifeActivities: (params) => client.get("/life/activities", { params }),
  lifeAwards: (params) => client.get("/life/awards", { params }),
  logLifeAward: (payload) => client.post("/life/awards", payload),
  lifeLeadership: (params) => client.get("/life/leadership", { params }),
  appointLeader: (payload) => client.post("/life/leadership", payload),
  lifePastoral: (params) => client.get("/life/pastoral", { params }),
  addPastoral: (payload) => client.post("/life/pastoral", payload),
  lifeStudentProfile: (studentId) => client.get(`/life/profile/${studentId}`),
  lifeKpis: () => client.get("/life/kpis"),
  lifeParentChild: (studentId) => client.get(`/life/parent/child/${studentId}`),
  // finance
  financeSummary: () => client.get("/finance/summary"),
  invoices: (params) => client.get("/finance/invoices", { params }),
  fees: () => client.get("/finance/fees"),
  // operations
  admissions: () => client.get("/admissions"),
  createAdmission: (d) => client.post("/admissions", d),
  updateAdmission: (id, d) => client.patch(`/admissions/${id}`, d),
  boardingHouses: () => client.get("/boarding/houses"),
  boardingStudents: () => client.get("/boarding/students"),
  transportRoutes: () => client.get("/transport/routes"),
  medicalRecords: () => client.get("/medical/records"),
  behaviourIncidents: () => client.get("/behaviour/incidents"),
  libraryBooks: () => client.get("/library/books"),
  libraryLoans: () => client.get("/library/loans"),
  sportsClubs: () => client.get("/sports/clubs"),
  hrStaff: () => client.get("/hr/staff"),
  // notifications / docs
  notifications: () => client.get("/notifications"),
  createNotification: (d) => client.post("/notifications", d),
  documents: () => client.get("/documents"),
  // portals
  parentOverview: () => client.get("/portal/parent/overview"),
  studentOverview: () => client.get("/portal/student/overview"),
  teacherOverview: () => client.get("/portal/teacher/overview"),
};
